import React, {Fragment, useCallback, useMemo, useState} from 'react';
import {useField, useForm} from 'react-final-form';
import {Autocomplete} from 'mui-rff';
import {Checkbox, MenuItem, Typography} from '@mui/material';
// stub for native Set object
import Set from 'lodash/_Set';
import {styled} from '@mui/material/styles';

const StyledMenuItem = styled(MenuItem)(()=>( {
    '&.MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: 'inherit',
    },
}));

const UTIL_OPTS = ['__ALL__', '__CLEAR__'];

const filterFn = (opts, {getOptionLabel, inputValue})=> {
    return opts.filter((opt)=> UTIL_OPTS.includes(opt.value)||getOptionLabel(opt).toLowerCase().includes(inputValue.toLowerCase()));
};

export default function EnhancedAutocomplete({
                                                 name, label, options,
                                                 disabled = false,
                                                 getOptionLabel = ({label}) => label,
                                                 getOptionValue = ({value}) => value,
                                                 getOptionDisabled = () => false,
                                                 getOptionColor,
                                                 renderOption,
                                             }) {
    const {change} = useForm();
    const {input: {value: selected}} = useField(name);
    // controlled state for text input - required for filtering
    const [inputText, setInputText] = useState('');
    // array of currently filtered options - should match logic behind autocomplete filtering
    const [visibleOptions, visibleEnabledOptions] = useMemo(() => {
        const txt = inputText.toLowerCase();
        const visibleOptions = options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(txt));
        const visibleEnabledOptions = visibleOptions.filter((option) => !getOptionDisabled(option));
        return [visibleOptions, visibleEnabledOptions];
    }, [options, getOptionLabel, getOptionDisabled, inputText]);
    // number of selected items among currently visible options
    const [clearableOptionsCount, allVisibleOptionsSelected] = useMemo(() =>
        [
            visibleEnabledOptions.filter((opt) => (selected).includes(getOptionValue(opt))).length,
            visibleEnabledOptions.every((opt) => selected.includes(getOptionValue(opt))),
        ], [visibleEnabledOptions, getOptionValue, selected]);
    // internal utility options that act as selection buttons
    const utilOptions = useMemo(()=>[
        {value: UTIL_OPTS[0], label: `Select All (${visibleEnabledOptions.length})`, disabled: allVisibleOptionsSelected},
        {value: UTIL_OPTS[1], label: `Clear All (${clearableOptionsCount})`, disabled: clearableOptionsCount===0},
    ], [visibleEnabledOptions.length, clearableOptionsCount, allVisibleOptionsSelected]);
    // full list of options,including util ones
    const opts = useMemo(()=>utilOptions
            .concat(options),
        [utilOptions, options]);


    const onInputChange = useCallback((e, value, reason) => {
        // for some reason, autocomplete tends to 'spam' reset events - so we ignore those and clear input manually on close
        if (reason === 'reset') {
            return;
        }
        setInputText(value);
    }, [setInputText]);

    const handleSelectAll = () => {
        // picking already selected values - using sets to make sure selection is unique
        const selectedValuesSet = new Set(selected);
        // picking visible values
        const visibleValuesSet = new Set(visibleEnabledOptions.map(getOptionValue));
        // joining those (instead of overwriting)
        const newSelectionSet = selectedValuesSet.union(visibleValuesSet);
        newSelectionSet.delete(UTIL_OPTS[0]);
        change(name, Array.from(newSelectionSet));
    };
    const handleClearAll = () => {
        const selectedValuesSet = new Set(selected);
        // picking visible values
        const visibleValuesSet = new Set(visibleEnabledOptions.map(getOptionValue));
        // picking values that are selected and present among visible values
        const visibleValuesToClear = selectedValuesSet.intersection(visibleValuesSet);
        // removing selected visible values from selection
        const newSelectionSet = selectedValuesSet.symmetricDifference(visibleValuesToClear);
        newSelectionSet.delete(UTIL_OPTS[1]);
        change(name, Array.from(newSelectionSet));
    };

    const onChange = (e, values, reason, {option: {value}})=>{
        if (!UTIL_OPTS.includes(value)) {
            return;
        }
        switch (value) {
            case UTIL_OPTS[0]: {
                handleSelectAll();
                return;
            }
            case UTIL_OPTS[1]: {
                handleClearAll();
                return;
            }
        }
    };
    const onMenuClose = useCallback(() => {
        setInputText('');
    }, [setInputText]);

    const internalRenderOption = useCallback((props, option, details) => {
        if (UTIL_OPTS.includes(option.value)) {
            return <StyledMenuItem {...props}><Typography textTransform='uppercase' m='auto'>{getOptionLabel(option)}</Typography></StyledMenuItem>;
        }
        return (
          <StyledMenuItem {...props}>{
            renderOption?
                renderOption(option, details):
                (<Fragment><Checkbox color={getOptionColor?getOptionColor(option):'primary'} checked={details.selected} />{getOptionLabel(option)}</Fragment>)
}
          </StyledMenuItem>
        );
    }, [getOptionLabel, getOptionColor, renderOption]);

    const getOptionDisabledInternal = useCallback((option)=>{
        if (UTIL_OPTS.includes(option.value)) {
            return option.disabled;
        }
        return getOptionDisabled(option);
    }, [getOptionDisabled]);

    return (
      <Autocomplete
        fullWidth
        disabled={disabled}
        name={name}
        label={label}
        options={opts}
        multiple
        onChange={onChange}
        filterOptions={filterFn}
        disableCloseOnSelect
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        getOptionDisabled={getOptionDisabledInternal}
        renderOption={internalRenderOption}
        inputValue={inputText}
        onInputChange={onInputChange}
        onClose={onMenuClose}
        disableClearable
        openOnFocus
      />
    );
}
